/**
 * Notifications feed (Agents Phase 4, piece 2 — design §4, §6) — now the module's single
 * "what needs me" surface (the managed Approvals tab folded in here).
 *
 * Default appearance = a calm COMPACT LIST of typed, prioritized signals
 * (`blocking_question` > `sign_off` > `done`, with a failure variant):
 *   - Needs your call — a run is stuck and needs a plain-language decision (answer in chat).
 *   - Sign-off needed — a run prepared a consequential action awaiting approval.
 *   - Done / Failed    — a run finished (success or failure), informational.
 *
 * Clicking a row EXPANDS IT INLINE into its detail (single-open accordion):
 *   - sign_off          → the inline sign-off card (Approve / Edit / Reject — record-only,
 *                         never sends/executes; design §5.3).
 *   - blocking_question → the question + "Answer in chat →" (scopes the chat to the run).
 *   - done / failed     → the outcome line.
 */
import { useState } from "react";
import type { AgentNotification, NotificationKind } from "./fixtures-deep";
import { NOTIF_PRIORITY, isUnresolvedNotification } from "./fixtures-deep";
import { StatusDot } from "./badges";
import { useNotifications, useRuns } from "./useAgentsUi";
import { InlineSignOffCard } from "./run-timeline";

const KIND_GLYPH: Record<NotificationKind, string> = {
  blocking_question: "?",
  sign_off: "✎",
  done: "✓",
};
const KIND_LABEL: Record<NotificationKind, string> = {
  blocking_question: "Needs your call",
  sign_off: "Sign-off needed",
  done: "Done",
};

/** Priority order (design §4): blocking_question first, then sign_off, then done. */
export function sortByPriority(items: AgentNotification[]): AgentNotification[] {
  return [...items].sort(
    (a, b) => NOTIF_PRIORITY[a.kind] - NOTIF_PRIORITY[b.kind],
  );
}

/** Count of signals still needing the human (sign_off + blocking_question). */
export function unresolvedCount(items: AgentNotification[]): number {
  return items.filter(isUnresolvedNotification).length;
}

export function NotificationsFeed({
  onSteer,
}: {
  /** Scope the chat to a run and reveal chat (for "answer in chat" on a blocking question). */
  onSteer?: (agentId: string, runId: string) => void;
}) {
  const { data: items = [], isLoading } = useNotifications();
  const [openId, setOpenId] = useState<string | null>(null);

  if (isLoading) return <div className="agui-stub">Loading notifications…</div>;
  if (items.length === 0) {
    return (
      <div className="agui-stub agui-stub--view">
        Nothing to report. When a run needs your call, prepares an action for
        sign-off, or finishes, it shows up here.
      </div>
    );
  }

  const sorted = sortByPriority(items);

  return (
    <div className="agui-notifs">
      <ul className="agui-notiflist">
        {sorted.map((n) => (
          <NotificationRow
            key={n.id}
            notif={n}
            open={openId === n.id}
            onToggle={() =>
              setOpenId((cur) => (cur === n.id ? null : n.id))
            }
            onSteer={onSteer}
          />
        ))}
      </ul>
    </div>
  );
}

function NotificationRow({
  notif: n,
  open,
  onToggle,
  onSteer,
}: {
  notif: AgentNotification;
  open: boolean;
  onToggle: () => void;
  onSteer?: (agentId: string, runId: string) => void;
}) {
  const failed = n.kind === "done" && n.outcome === "failure";
  const kindClass = failed ? "done-failure" : n.kind;
  const aria = `${failed ? "Failed" : KIND_LABEL[n.kind]}: ${n.summary}`;

  return (
    <li className={`agui-notifrow-wrap${open ? " is-open" : ""}`}>
      <button
        type="button"
        className={`agui-notifrow agui-notifrow--${kindClass}`}
        aria-expanded={open}
        onClick={onToggle}
        aria-label={aria}
      >
        <span
          className={`agui-notifrow__glyph agui-notifrow__glyph--${kindClass}`}
          aria-hidden="true"
        >
          {failed ? "!" : KIND_GLYPH[n.kind]}
        </span>
        <span className="agui-notifrow__main">
          <span className="agui-notifrow__top">
            <StatusDot color={n.agentColor} />
            <span className="agui-notifrow__agent">{n.agentName}</span>
            <span
              className={`agui-notifrow__kind agui-notifrow__kind--${kindClass}`}
            >
              {failed ? "Failed" : KIND_LABEL[n.kind]}
            </span>
          </span>
          <span className="agui-notifrow__sum">{n.summary}</span>
        </span>
        <span className="agui-notifrow__when">{n.when}</span>
      </button>

      {open ? (
        <div className="agui-notifrow__detail">
          <NotificationDetail notif={n} onSteer={onSteer} />
        </div>
      ) : null}
    </li>
  );
}

/** The expanded detail for a notification — the sign-off card, or a light outcome panel. */
function NotificationDetail({
  notif: n,
  onSteer,
}: {
  notif: AgentNotification;
  onSteer?: (agentId: string, runId: string) => void;
}) {
  if (n.kind === "sign_off") {
    return <SignOffDetail notif={n} />;
  }
  return (
    <div className="agui-notifdetail">
      <p className="agui-notifdetail__text">{n.summary}</p>
      {n.kind === "blocking_question" && onSteer ? (
        <button
          type="button"
          className="agui-link"
          onClick={() => onSteer(n.agentId, n.runId)}
        >
          Answer in chat →
        </button>
      ) : null}
    </div>
  );
}

/** Looks up the run's prepared sign-off (by run id) and renders the inline sign-off card. */
function SignOffDetail({ notif: n }: { notif: AgentNotification }) {
  const { data: runs = [], isLoading } = useRuns(n.agentId);
  if (isLoading) return <div className="agui-stub">Loading…</div>;
  const run = runs.find((r) => r.id === n.runId);
  if (!run?.awaitingSignOff) {
    return (
      <div className="agui-notifdetail">
        <p className="agui-notifdetail__text">
          This sign-off is no longer pending.
        </p>
      </div>
    );
  }
  return <InlineSignOffCard signOff={run.awaitingSignOff} />;
}
